import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Row,
  Upload
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { consultationService } from "../../../services/medicalConsultation";
import { submitPrescriptionDocuments } from "../../../services/reservation";
import { uploadAssetController, allowedFileCriteria } from '../../../utils/fileUtils';

const UploadDocumentsModal = ({
  countryCode,
  countrySettings,
  modalVisible,
  setModalVisible,
  prescriptionIndex,
  prescriptionItem,
  setPrescriptionItems,
  medicalConsultationId,
  medicalPrescriptionId,
  reevaluationData,
  fetchReevaluationData
}) => {
  const bodyBreakpointLg = countrySettings.documentsModal.bodyBreakpointLg[countryCode];
  const renderPrescriptionItemName = countrySettings.documentsModal.renderPrescriptionItemName[countryCode];
  const justifyRow = countrySettings.documentsModal.justifyRow[countryCode];
  const saveButtonBreakpoint = countrySettings.documentsModal.saveButtonBreakpoint[countryCode];
  const renderSuccessContent = countrySettings.documentsModal.renderSuccessContent[countryCode];
  const sendDocuments = countrySettings.documentsModal.sendDocuments[countryCode];

  const [form] = Form.useForm();
  const [loading, setLoading] = useState({
    sendFile: false,
    deleteFile: false,
    uploadFiles: false,
  });
  const { t } = useTranslation();

  const closeModal = () => {
    setModalVisible({ open: false, success: false });
  };

  const sendFile = async (
    file,
    index,
    exam_document_id,
    name,
    isNew,
    medicalPrescriptionDocumentId,
  ) => {

    if (!allowedFileCriteria(t, file)) {
      return;
    }

    try {
      setLoading(loading => { return { ...loading, sendFile: true } });
  
      const response = await consultationService.sendPatientDocument({
        medical_consultation_id: medicalConsultationId,
        medical_prescription_id: medicalPrescriptionId,
        exam_id: prescriptionItem.examId,
        document: {
          new_document: isNew,
          prescription_key: prescriptionItem.key,
          exam_document_id: exam_document_id,
          name,
          file_data: {
            file_name: file.name,
            file_size: file.size,
            content_type: file.type
          },
          ...(medicalPrescriptionDocumentId ? { medical_prescription_document_id: medicalPrescriptionDocumentId } : {}),
        }
      })

      if (response?.exam_document_id) {
        await uploadAssetController({
          file,
          object_field: 'document',
          object_id: response?.exam_document_id,
          object_type: 'ExamsDocument',
        })

        const documents = prescriptionItem.documents;
        documents[index] = {
          name: name,
          exam_document_id: response?.exam_document_id,
          medical_prescription_document_id: response?.medical_prescription_document_id,
          file_name: file.name,
          new_document: isNew
        };

        setPrescriptionItems(prescriptionItems => {
          const _prescriptionItems = [...prescriptionItems];
          _prescriptionItems[prescriptionIndex].documents = documents;
          return _prescriptionItems;
        });

        await fetchReevaluationData({ medicalConsultationId });

        message.success({
          content: "Archivo cargado exitosamente.",
          style: {
            marginTop: "35vh",
          },
        });
      } else {
        throw new Error('Ha ocurrido un error al cargar el archivo. Inténtalo nuevamente');
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: `Ha ocurrido un error al guardar el archivo`,
        style: {
          marginTop: '35vh',
        }
      });
    } finally {
      setLoading(loading => { return { ...loading, sendFile: false } });
    }
  };

  const handleRemoveDocument = async (index, item) => {
    try {
      setLoading(loading => { return { ...loading, deleteFile: true } });

      const response = await deleteDocument(item);
      let documents = prescriptionItem.documents;
      let newDocumentsCounter = 0;

      if (item.new_document) {
        documents.splice(index, 1);
      } else {
        documents[index] = {
          exam_document_id: null,
          file_name: null,
          name: item.name,
          new_document: item.new_document,
          medical_prescription_document_id: response?.medical_prescription_document_id
        };
      }

      documents = [
        ...documents.filter((document) => !document.new_document || document.exam_document_id),
        ...documents.filter((document) => document.new_document && !document.exam_document_id),
      ]

      documents.forEach((document) => {
        if (document.new_document) {
          if (document.exam_document_id) {
            response?.documents?.forEach((responseDocument) => {
              if (document.exam_document_id === responseDocument.exam_document_id) {
                document.name = responseDocument.name;
                newDocumentsCounter += 1;
              }
            });
          } else {
            document.name = `Nuevo documento ${newDocumentsCounter + 1}`;
            newDocumentsCounter += 1;
          }
        }
      });

      setPrescriptionItems(prescriptionItems => {
        const _prescriptionItems = [...prescriptionItems];
        _prescriptionItems[prescriptionIndex].documents = documents;
        return _prescriptionItems;
      });

      await fetchReevaluationData({ medicalConsultationId });
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Ha ocurrido un error al eliminar el archivo. Inténtalo nuevamente',
        style: {
          marginTop: '35vh'
        }
      });
    } finally {
      setLoading(loading => { return { ...loading, deleteFile: false } });
    }
  };

  const deleteDocument = async (item) => {
    const params = {
      exam_document_id: item.exam_document_id,
      exam_id: prescriptionItem.examId,
      prescription_key: prescriptionItem.key,
      name: item.name,
      new_document: item.new_document,
      medical_consultation_id: medicalConsultationId,
      medical_prescription_id: medicalPrescriptionId,
      medical_prescription_document_id: item.medical_prescription_document_id,
    };

    const response = await consultationService.deletePatientDocument(params);

    if (response?.state === 'success') {
      message.success({
        content: "Archivo eliminado.",
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });

      await fetchReevaluationData({ medicalConsultationId });
    } else {
      throw new Error('Ha ocurrido un error al eliminar el archivo. Inténtalo nuevamente');
    }
    return response;
  };

  const addNewDocument = () => {
    const documents = prescriptionItem?.documents?.filter((document) => document.new_document);
    const documentsQuantity = documents?.length || 0;
    setPrescriptionItems(prescriptionItems => {
      const _prescriptionItems = [...prescriptionItems];
      _prescriptionItems[prescriptionIndex].documents.push({
        exam_document_id: null,
        name: `Nuevo documento ${documentsQuantity + 1}`,
        new_document: true,
      });

      return _prescriptionItems;
    });
  };
  
  const onFinish = async () => {
    const allFilesUploaded = prescriptionItem.documents.every(item => item.file_name);

    if (!allFilesUploaded) {
      message.error({
        content: 'Debes cargar todos los documentos para continuar',
        style: {
          marginTop: '40vh',
        },
      });

      return;
    } 

    try {
      setLoading(loading => ({ ...loading, uploadFiles: true }));

      if (!sendDocuments) {
        message.success({
          content: 'Documentos agregados exitosamente',
          style: {
            marginTop: '40vh',
          },
        });

        closeModal();

        return;
      }

      const allDocuments = (reevaluationData?.prescription_extra_data || [])
        .flatMap(prescriptionItem => prescriptionItem.documents || []);
      const hasDocuments = allDocuments.length > 0;
      const allDocumentsPendingRevision = allDocuments.every(doc => doc.state === "pending_revision");
      const finishSubmitPrescriptionDocuments = hasDocuments && allDocumentsPendingRevision;
      let response = null;

      if (finishSubmitPrescriptionDocuments) {
        response = await submitPrescriptionDocuments(
          { medicalConsultationDetailId: medicalConsultationId }
        );
      }

      if (!finishSubmitPrescriptionDocuments || response?.success) {
        if (renderSuccessContent) {
          setModalVisible(prev => ({ ...prev, success: true }));
          
          return;
        }

        message.success({
          content: 'Documentos agregados exitosamente',
          style: {
            marginTop: '40vh'
          }
        });
      } else {
        message.error({
          content: 'Error al enviar documentos. Inténtalo nuevamente',
          style: {
            marginTop: '40vh',
          },
        });

        throw new Error(response);
      }
    } catch (error) {
      console.error('Error al enviar documentos: ', error);
    } finally {
      setLoading(loading => ({ ...loading, uploadFiles: false }));
    }
  };


  return (
    <Modal
      open={modalVisible.open}
      onCancel={closeModal}
      centered
      closable
      footer={false}
      style={{ maxHeight: '90vh' }}
      className={`documents-modal --${countryCode}`}
    >
      <Row justify="center">
      {
        modalVisible.success ?
          <Col lg={24} xs={24} className='success-upload-wrapper'>
            <Row justify='center'>
              <Col>
                <span className='icon-brand-feedback' />
              </Col>
              <Col lg={24} xs={24}>
                <h1 className='text-center'>Carga de documentos finalizada con éxito</h1>
                <p className='text-center'>Los documentos pasarán a estado <strong>En revisión</strong>. <br/> Se les notificará por correo electrónico al paciente y la empresa.</p>
              </Col>
            </Row>
          </Col>
        :
          <Col lg={24} xs={24}>
            <Form
              form={form}
              size="large"
              onFinish={onFinish}
            >
              <Row justify="center">
                <Col lg={24} xs={24}>
                  <h1 className="text-center mb-10">{t('reservations.uploadDocumentsModal.title')}</h1>
                </Col>
                <Col lg={24} xs={24}>
                  <p className="text-center" dangerouslySetInnerHTML={{ __html: t('reservations.uploadDocumentsModal.disclaimer') }} />
                </Col>
              </Row>
              <Row justify="center">
                <Col lg={bodyBreakpointLg} xs={24}>
                {
                  renderPrescriptionItemName && <p className='bold mb-5'>{prescriptionItem?.name}</p>
                }
                  <Row justify="center" gutter={[0, 20]}>
                    {
                      prescriptionItem?.documents.map((item, index) => {
                  
                        return (
                          <Col lg={24} xs={24} key={`R${index}`} className="gray-item pl-40 pr-40 pt-20 pb-20">
                            <Row justify="center">
                              <Col lg={20} xs={24}>
                                {
                                  (item && item.new_document) &&
                                  <Row justify="end">
                                    <Col>
                                      <Button
                                        disabled={loading.deleteFile}
                                        onClick={
                                          () => handleRemoveDocument(index, item)
                                        }
                                        className="cmt-button secondary non-outline floating p-0"
                                        type="primary"
                                        style={{ boxShadow: 'none' }}
                                      >
                                        <CloseOutlined style={{ fontSize: 20, color: '#223A70' }} />
                                      </Button>
                                    </Col>
                                  </Row>
                                }
                              </Col>
                              <Col lg={20} xs={24}>
                                <p className='document-name'>{item.name}</p>
                              </Col>
                              <Col lg={20} xs={24}>
                                <Row gutter={15} align="middle">
                                  <Col lg={22} xs={22}>
                                    {
                                      item.exam_document_id ?
                                        <p className="file-text mb-0">{item.file_name}</p>
                                        :
                                        <Form.Item
                                          name={item.name}
                                          className="mb-0"
                                        >
                                          <Upload
                                            name="logo"
                                            listType="picture"
                                            beforeUpload={(file) => {
                                              sendFile(file, index, item.exam_document_id, item.name, item.new_document, item?.medical_prescription_document_id)
                                              return false;
                                            }}
                                            onChange={(info) => {
                                              if (info.file.status === 'error') {
                                                message.error({
                                                  content: 'Error al cargar el archivo. Inténtalo nuevamente',
                                                  style: {
                                                    marginTop: '40vh'
                                                  }
                                                });
                                              }
                                            }}
                                          >
                                            <Button
                                              loading={loading.sendFile}
                                              className="cmt-button secondary outline"
                                              type="primary"
                                              block
                                            >
                                              <PaperClipOutlined className="mr-10" style={{ color: '#223A70', fontSize: 20 }} />
                                              Cargar documento aquí
                                            </Button>
                                          </Upload>
                                        </Form.Item>
                                    }
                                  </Col>
                                  <Col lg={2} xs={2}>
                                    <Button
                                      loading={loading.deleteFile}
                                      onClick={
                                        () => handleRemoveDocument(index, item)
                                      }
                                      disabled={!item.file_name}
                                      className={item.file_name ? 'cmt-button secondary non-outline p-0' : 'cmt-button disabled non-outline p-0'}
                                      type="primary"
                                      style={{ boxShadow: 'none' }}
                                    >
                                      {
                                        !loading.deleteFile && <span className='icon-system-delete' />
                                      }
                                    </Button>
                                  </Col>
                                </Row>
                              </Col>
                              <Col lg={20} xs={24}>
                                <p className="small mt-5 mb-0">{t('reservations.uploadDocumentsModal.fileTypes')}</p>
                              </Col>
                            </Row>
                          </Col>
                        )
                      })
                    }
                  </Row>
                </Col>
                <Col lg={15} xs={24}>
                  <Row justify={justifyRow} className="mt-40">
                    <Col>
                      <Button
                        onClick={addNewDocument}
                        className={(loading.sendFile || loading.deleteFile || loading.uploadFiles) ? "cmt-button disabled pr-20 pl-20" : "cmt-button secondary pr-20 pl-20"}
                        type="primary"
                        size="middle"
                        disabled={loading.sendFile || loading.deleteFile || loading.uploadFiles}
                      >
                        <i className="icon-system-plus mr-5" />
                        Agregar otro documento
                      </Button>
                    </Col>
                  </Row>
                </Col>
                <Col lg={15} xs={24} className="mt-30">
                  <Row justify="center">
                    <Col lg={saveButtonBreakpoint} xs={24}>
                      <Button
                        block
                        loading={loading.uploadFiles}
                        type="primary"
                        htmlType="submit"
                        disabled={loading.sendFile || loading.deleteFile || loading.uploadFiles}
                        className={(loading.sendFile || loading.deleteFile || loading.uploadFiles) ? "cmt-button disabled pr-20 pl-20" : "cmt-button primary pr-20 pl-20"}
                      >
                        Guardar
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
      }
      </Row>
    </Modal>
  );
};

export default UploadDocumentsModal;
